import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook that checks if the current user has the 'monitor' role
 * and redirects them to /monitor if they try to access other routes
 */
export const useMonitorRedirect = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMonitor, setIsMonitor] = useState<boolean | null>(null);
  const [isCheckingRole, setIsCheckingRole] = useState(true);

  useEffect(() => {
    if (!user) {
      setIsMonitor(null);
      setIsCheckingRole(false);
      return;
    }

    const checkMonitorRole = async () => {
      try {
        console.log("🔍 useMonitorRedirect - checking monitor role for user:", user.id);
        
        const { data: hasMonitorRole, error } = await supabase
          .rpc('has_role', { _user_id: user.id, _role: 'monitor' });

        if (error) {
          console.error("❌ useMonitorRedirect - error checking role:", error);
          // Fallback: check profiles table
          const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', user.id)
            .single();
          
          const isMonitorFallback = profile?.role === 'monitor';
          setIsMonitor(isMonitorFallback);
          
          if (isMonitorFallback && location.pathname !== '/monitor') {
            console.log("🎯 useMonitorRedirect - redirecting monitor to /monitor (fallback)");
            navigate('/monitor', { replace: true });
          }
        } else {
          console.log("✅ useMonitorRedirect - has_role result:", hasMonitorRole);
          setIsMonitor(hasMonitorRole === true);

          if (hasMonitorRole && location.pathname !== '/monitor') {
            console.log("🎯 useMonitorRedirect - redirecting monitor to /monitor");
            navigate('/monitor', { replace: true });
          }
        }
      } catch (error) {
        console.error("❌ useMonitorRedirect - unexpected error:", error);
        setIsMonitor(false);
      } finally {
        setIsCheckingRole(false);
      }
    };

    checkMonitorRole();
  }, [user, location.pathname, navigate]);

  return { isMonitor, isCheckingRole };
};

/**
 * Utility function to check if a user is a monitor (for use outside React components)
 */
export const checkIsMonitor = async (userId: string): Promise<boolean> => {
  try {
    const { data: hasMonitorRole, error } = await supabase
      .rpc('has_role', { _user_id: userId, _role: 'monitor' });

    if (error) {
      // Fallback to profiles table
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single();
      
      return profile?.role === 'monitor';
    }

    return hasMonitorRole === true;
  } catch (error) {
    console.error("❌ checkIsMonitor - error:", error);
    return false;
  }
};
